<template>
  <main>
    <div class="row">
      <div class="col-12 col-md-4 col-lg-2">
        <div
          class="d-flex align-items-center justify-content-between text--medium text--normal-size pt-1"
        >
          {{ equipmentResourceListData.equipmentName }}

          <div
            v-if="equipmentResourceListData.tag.length"
            :id="'tag-popover' + equipmentResourceListData.id"
          >
            <feather-icon icon="TagIcon" size="12" class="tag-icon--style" />
          </div>
          <b-popover
            v-if="equipmentResourceListData.tag.length"
            ref="popover"
            :target="'tag-popover' + equipmentResourceListData.id"
            triggers="hover"
            placement="right"
            container="my-container"
            boundary="window"
          >
            <template v-slot:title>
              <div class="d-flex justify-content-between align-items-left">
                <span class="text--normal-size">Tag</span>
              </div>
            </template>
            <div class="badge__container">
              <b-badge
                v-for="(tag, index) in equipmentResourceListData.tag"
                :key="index"
                variant="light-primary"
                class="badge--style tag--space"
              >
                {{ tag }}
              </b-badge>
            </div>
          </b-popover>
        </div>
        <div class="text--light text--small-size text--color-secondary">
          {{ equipmentResourceListData.equipmentIp }}
        </div>
      </div>
      <div class="col-12 col-md-8 col-lg-4 pt-1">
        <span class="text--normal-size">{{
          equipmentResourceListData.description
        }}</span>
      </div>
      <div
        class="col-12 col-md-4 col-lg-2 mt-1 mt-md-0 pl-lg-0 d-flex align-items-center"
      >
        <div class="d-flex flex-column w-100">
          <div class="mb-0 text--mini-size">CPU</div>
          <div class="d-flex align-items-center">
            <div class="d-flex progress--width h-100 flex-column">
              <div class="w-100">
                <atom-bootstrap-progress-bar
                  :color="resourceStatus('cpu')"
                  :value="parseFloat(equipmentResourceListData.cpuValue)"
                ></atom-bootstrap-progress-bar>
              </div>
            </div>
            <div
              class="d-flex justify-content-end align-items-center percent--width"
            >
              <div
                class="text--extra-bold text--normal-size"
                :class="`text-${resourceStatus('cpu')}`"
              >
                {{ equipmentResourceListData.cpuValue }} %
              </div>
            </div>
            <div
              class="alert--width alert__icon--padding d-flex align-items-center justify-content-end"
            >
              <div
                :id="'cpu-alert-icon' + equipmentResourceListData.id"
                class="d-flex justify-content-end align-items-center"
              >
                <feather-icon
                  :ref="'cpu-alert-icon' + equipmentResourceListData.id"
                  icon="AlertTriangleIcon"
                  class="alert__icon--active-style"
                />
              </div>
              <b-tooltip
                :target="'cpu-alert-icon' + equipmentResourceListData.id"
                placement="right"
                boundary="window"
                variant="warning"
              >
                <div class="textbox__warning-value--style">
                  <span>Warning</span>
                  <span>{{
                    equipmentResourceListData.cpuWarningValue
                      ? equipmentResourceListData.cpuWarningValue +
                        " " +
                        policyUnit("cpu")
                      : "-"
                  }}</span>
                </div>
                <div class="textbox__danger-value--style">
                  <span>Critical</span>
                  <span>{{
                    equipmentResourceListData.cpuCriticalValue
                      ? equipmentResourceListData.cpuCriticalValue +
                        " " +
                        policyUnit("cpu")
                      : "-"
                  }}</span>
                </div>
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-12 col-md-4 col-lg-2 mt-1 mt-md-0 pl-lg-0 d-flex align-items-center"
      >
        <div class="d-flex flex-column w-100">
          <div class="mb-0 text--mini-size">MEMORY</div>
          <div class="d-flex align-items-center">
            <div class="d-flex progress--width h-100 flex-column">
              <div class="w-100">
                <atom-bootstrap-progress-bar
                  :color="resourceStatus('memory')"
                  :value="parseFloat(equipmentResourceListData.memoryValue)"
                ></atom-bootstrap-progress-bar>
              </div>
            </div>
            <div
              class="d-flex justify-content-end align-items-center percent--width"
            >
              <div
                class="text--extra-bold text--normal-size"
                :class="`text-${resourceStatus('memory')}`"
              >
                {{ equipmentResourceListData.memoryValue }} %
              </div>
            </div>
            <div
              class="alert--width alert__icon--padding d-flex align-items-center justify-content-end"
            >
              <div
                :id="'memory-alert-icon' + equipmentResourceListData.id"
                class="d-flex justify-content-end align-items-center"
              >
                <feather-icon
                  :ref="'memory-alert-icon' + equipmentResourceListData.id"
                  icon="AlertTriangleIcon"
                  class="alert__icon--active-style"
                />
              </div>
              <b-tooltip
                :target="'memory-alert-icon' + equipmentResourceListData.id"
                placement="right"
                boundary="window"
                variant="warning"
              >
                <div class="textbox__warning-value--style">
                  <span>Warning</span>
                  <span>{{
                    equipmentResourceListData.memoryWarningValue
                      ? equipmentResourceListData.memoryWarningValue +
                        " " +
                        policyUnit("memory")
                      : "-"
                  }}</span>
                </div>
                <div class="textbox__danger-value--style">
                  <span>Critical</span>
                  <span>{{
                    equipmentResourceListData.memoryCriticalValue
                      ? equipmentResourceListData.memoryCriticalValue +
                        " " +
                        policyUnit("memory")
                      : "-"
                  }}</span>
                </div>
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-12 col-md-4 col-lg-2 mt-1 mt-md-0 pl-lg-0 d-flex align-items-center"
      >
        <div class="d-flex flex-column w-100">
          <div class="mb-0 text--mini-size">STORAGE USAGE</div>
          <div class="d-flex align-items-center">
            <div class="d-flex progress--width h-100 flex-column">
              <div class="w-100">
                <atom-bootstrap-progress-bar
                  :color="resourceStatus('storage')"
                  :value="parseFloat(equipmentResourceListData.storageValue)"
                ></atom-bootstrap-progress-bar>
              </div>
            </div>
            <div
              class="d-flex justify-content-end align-items-center percent--width"
            >
              <div
                class="text--extra-bold text--normal-size"
                :class="`text-${resourceStatus('storage')}`"
              >
                {{ equipmentResourceListData.storageValue }} %
              </div>
            </div>
            <div
              class="alert--width alert__icon--padding d-flex align-items-center justify-content-end"
            >
              <div
                :id="'storage-alert-icon' + equipmentResourceListData.id"
                class="d-flex justify-content-end align-items-center"
              >
                <feather-icon
                  :ref="'storage-alert-icon' + equipmentResourceListData.id"
                  icon="AlertTriangleIcon"
                  class="alert__icon--active-style"
                />
              </div>
              <b-tooltip
                :target="'storage-alert-icon' + equipmentResourceListData.id"
                placement="right"
                boundary="window"
                variant="warning"
              >
                <div class="textbox__warning-value--style">
                  <span>Warning</span>
                  <span>{{
                    equipmentResourceListData.storageWarningValue
                      ? equipmentResourceListData.storageWarningValue +
                        " " +
                        policyUnit("storage")
                      : "-"
                  }}</span>
                </div>
                <div class="textbox__danger-value--style">
                  <span>Critical</span>
                  <span>{{
                    equipmentResourceListData.storageCriticalValue
                      ? equipmentResourceListData.storageCriticalValue +
                        " " +
                        policyUnit("storage")
                      : "-"
                  }}</span>
                </div>
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr />
  </main>
</template>

<script>
import { BBadge, BPopover, VBTooltip, BTooltip, BProgress } from "bootstrap-vue"
import AtomBootstrapProgressBar from "@/components/atoms/graphs/AtomBootstrapProgressBar.vue"
import Ripple from "vue-ripple-directive"
export default {
  components: {
    BBadge,
    BPopover,
    VBTooltip,
    BTooltip,
    BProgress,
    AtomBootstrapProgressBar,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  name: "MoleculeEquipmentResource",
  props: {
    equipmentResourceListData: {
      type: Object,
      required: true,
    },
  },
  methods: {
    resourceStatus(type) {
      if (type === "cpu") {
        if (this.equipmentResourceListData.cpuStatus === 0) return "primary"
        else if (this.equipmentResourceListData.cpuStatus === 1)
          return "warning"
        else return "danger"
      } else if (type === "memory") {
        if (this.equipmentResourceListData.memoryStatus === 0) return "primary"
        else if (this.equipmentResourceListData.memoryStatus === 1)
          return "warning"
        else return "danger"
      } else {
        if (this.equipmentResourceListData.storageStatus === 0) return "primary"
        else if (this.equipmentResourceListData.storageStatus === 1)
          return "warning"
        else return "danger"
      }
    },
    policyUnit(type) {
      if (type === "cpu") {
        if (
          this.equipmentResourceListData.cpuPolicyUnit.toLowerCase() !==
          "percent"
        )
          return this.equipmentResourceListData.cpuPolicyUnit
        else return "%"
      } else if (type === "memory") {
        if (
          this.equipmentResourceListData.memoryPolicyUnit.toLowerCase() !==
          "percent"
        )
          return this.equipmentResourceListData.memoryPolicyUnit
        else return "%"
      } else {
        if (
          this.equipmentResourceListData.storagePolicyUnit.toLowerCase() !==
          "percent"
        )
          return this.equipmentResourceListData.storagePolicyUnit
        else return "%"
      }
    },
    progressBg(value, criticalValue, warningValue) {
      if (value > criticalValue) {
        return "progress-bar-danger"
      } else if (value > warningValue) {
        return "progress-bar-warning"
      } else {
        return ""
      }
    },
    progressVariant(value, criticalValue, warningValue) {
      if (value > criticalValue) {
        return "danger"
      } else if (value > warningValue) {
        return "warning"
      } else {
        return "primary"
      }
    },
    textColor(value, criticalValue, warningValue) {
      if (value > criticalValue) {
        return "text-danger"
      } else if (value > warningValue) {
        return "text-warning"
      } else {
        return "text-primary"
      }
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin-top: 10px;
  margin-bottom: 10px;
}

.text--extra-bold {
  font-weight: 700;
}

.text--bold {
  font-weight: 600;
}

.text--medium {
  font-weight: 500;
}

.text--light {
  font-weight: 400;
}

.text--normal-size {
  font-size: 12px;
}

.text--mini-size {
  font-size: 10px;
}

.text--small-size {
  font-size: 10px;
}

.text--color-secondary {
  color: #b9b9c3;
}

.item--right {
  justify-self: end;
}

.badge--width {
  width: 68px;
}

.margin-top--custom {
  margin-top: 10px;
}

.margin-left--custom {
  margin-left: 10px;
}

.padding-top--custom {
  padding-top: 3px;
}

.padding-left--custom {
  padding-left: 8px;
}

.triangle-icon--padding {
  padding: 0 1rem 0 0;
}

.alert__icon--padding {
  margin-left: 0.5rem;
  margin-right: 0;
}

.textbox__warning-value--style {
  display: flex;
  justify-content: space-between;
  color: #ff9f43;
  font-weight: 400;
  font-size: 14px;
}

.textbox__danger-value--style {
  display: flex;
  justify-content: space-between;
  color: #ea5455;
  font-weight: 400;
  font-size: 14px;
}

.alert__icon--active-style {
  color: #b9b9c3;
}

.tag-icon--style {
  color: #9d95f5;
  margin-left: 8px;
}

.badge--style {
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag--space {
  margin-left: 4px;
  margin-bottom: 4px;
}

.padding__custom--value {
  padding: 10px 0 7px 0;
}

.progress--width {
  width: 70%;
}
.percent--width {
  width: 20%;
}
.alert--width {
  width: 10%;
}

.alert__icon--padding {
  margin-right: 0;
}

::v-deep.tooltip > .tooltip-inner {
  max-width: 171px !important;
  width: 171px !important;
}

::v-deep .progress {
  height: 8px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .progress--width {
    width: 60%;
  }
  .percent--width {
    width: 30%;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
  .triangle-icon--padding {
    padding: 8px 8px 0 0;
  }
  .progress--width {
    width: 40%;
  }
  .percent--width {
    width: 50%;
  }
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
  .progress--width {
    width: 45%;
  }
  .percent--width {
    width: 45%;
  }
}

// XXX-Large devices (1920px and up)
@media screen and (min-width: 1920px) {
  .progress--width {
    width: 60%;
  }
  .percent--width {
    width: 30%;
  }
}
</style>
